(() => {
  const VARIANT_UPDATE_EVENT = 'variant:update';

  class MyteaSellingPlans extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.addEventListener('change', this.handleChange);
      this.closest('.shopify-section')?.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.closest('.shopify-section')?.removeEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
    }

    handleChange = () => this.sync();

    handleVariantUpdate = (event) => {
      if (event.detail?.data?.productId !== this.dataset.productId || !event.detail?.data?.html) return;

      const currentMode = this.querySelector('input[name^="purchase_option_"]:checked')?.value || 'one-time';
      const currentPlan = this.querySelector('[data-mytea-selling-plan-frequencies] input:checked')?.value;
      const replacement = event.detail.data.html.querySelector(
        `mytea-selling-plans[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`
      );

      if (!replacement) {
        this.remove();
        return;
      }

      const newMode = replacement.querySelector(`input[name^="purchase_option_"][value="${currentMode}"]`);
      if (newMode instanceof HTMLInputElement && !newMode.disabled) newMode.checked = true;

      if (currentPlan) {
        const newPlan = replacement.querySelector(
          `[data-mytea-selling-plan-frequencies] input[value="${CSS.escape(currentPlan)}"]`
        );
        if (newPlan instanceof HTMLInputElement) newPlan.checked = true;
      }

      this.replaceWith(replacement);
      replacement.sync?.();
    };

    sync() {
      const selectedMode = this.querySelector('input[name^="purchase_option_"]:checked')?.value || 'one-time';
      const isSubscription = selectedMode === 'subscription';
      const frequencies = this.querySelector('[data-mytea-selling-plan-frequencies]');
      const planInput = this.querySelector('[data-mytea-selling-plan-input]');
      const selectedFrequency = this.querySelector('[data-mytea-selling-plan-frequencies] input:checked');
      const subscriptionPrice = this.querySelector('[data-mytea-subscription-price]');
      const subscriptionLabel = this.querySelector('[data-mytea-subscription-label]');
      const oneTimePrice = this.querySelector('[data-mytea-one-time-price]');
      const status = this.querySelector('[data-mytea-selling-plan-status]');

      this.querySelectorAll('.mytea-selling-plan-option').forEach((option) => {
        const radio = option.querySelector('input[name^="purchase_option_"]');
        option.classList.toggle('is-selected', radio?.checked === true);
      });

      if (frequencies instanceof HTMLElement) frequencies.hidden = !isSubscription;
      if (planInput instanceof HTMLInputElement) {
        planInput.disabled = !isSubscription;
        planInput.value = isSubscription && selectedFrequency instanceof HTMLInputElement ? selectedFrequency.value : '';
      }
      if (subscriptionPrice && selectedFrequency instanceof HTMLInputElement) {
        subscriptionPrice.textContent = selectedFrequency.dataset.price || '';
      }
      if (subscriptionLabel && selectedFrequency instanceof HTMLInputElement) {
        const saving = Number.parseInt(selectedFrequency.dataset.savingPercent || '0', 10);
        subscriptionLabel.textContent = saving > 0 ? `Subscribe & save ${saving}%` : 'Subscribe';
      }

      const productPrice = this.closest('.product-details')?.querySelector('product-price .price');
      if (productPrice) {
        const nextPrice = isSubscription
          ? selectedFrequency?.dataset?.price
          : oneTimePrice?.textContent?.trim();
        if (nextPrice) productPrice.textContent = nextPrice;
      }

      if (status) {
        const planName = selectedFrequency instanceof HTMLInputElement ? selectedFrequency.dataset.planName : '';
        status.textContent = isSubscription
          ? `Subscription selected${planName ? `: ${planName}` : ''}.`
          : 'One-time purchase selected.';
      }
    }
  }

  if (!customElements.get('mytea-selling-plans')) {
    customElements.define('mytea-selling-plans', MyteaSellingPlans);
  }

  const initializeProductNavigation = (root = document) => {
    root.querySelectorAll('[data-mytea-product-nav]:not([data-initialized])').forEach((navigation) => {
      navigation.dataset.initialized = 'true';
      const links = Array.from(navigation.querySelectorAll('a[href^="#"]'));
      const available = links
        .map((link) => ({ link, target: document.getElementById(link.hash.slice(1)) }))
        .filter(({ link, target }) => {
          link.hidden = !target;
          return target;
        });

      const activate = (activeLink) => {
        available.forEach(({ link }) => {
          const isActive = link === activeLink;
          link.classList.toggle('is-active', isActive);
          if (isActive) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };

      available.forEach(({ link }) => link.addEventListener('click', () => activate(link)));
      if (available[0]) activate(available[0].link);

      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible) return;
          activate(available.find(({ target }) => target === visible.target)?.link);
        },
        { rootMargin: '-20% 0px -68%', threshold: [0, 0.15, 0.5] }
      );
      available.forEach(({ target }) => observer.observe(target));
    });
  };

  const initializeReviewButtons = (root = document) => {
    root.querySelectorAll('[data-mytea-write-review]:not([data-initialized])').forEach((button) => {
      button.dataset.initialized = 'true';
      button.addEventListener('click', (event) => {
        const app = document.getElementById('mytea-product-reviews-app');
        const action = Array.from(app?.querySelectorAll('button, a') || []).find((item) =>
          /write\s+(a\s+)?review/i.test(item.textContent || '')
        );
        if (!action) return;
        event.preventDefault();
        action.click();
      });
    });
  };

  const initialize = (root = document) => {
    initializeProductNavigation(root);
    initializeReviewButtons(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
